import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Layout } from './components/Layout';
import { RouteLoading } from './components/RouteLoading';
import { useT } from './i18n';
import { RequireAuth } from './components/RequireAuth';
import { VersionBadge } from './components/VersionBadge';
import { RequireAdmin } from './features/admin/RequireAdmin';
import { LoginPage } from './features/auth/LoginPage';
import { ContactPage } from './features/contact/ContactPage';
import { HomePage } from './features/home/HomePage';

// Every page except the front door and the sign-in loads on demand, so a
// first visit downloads the home page and not the admin console, the manager
// dashboard and the booking flow as well.
const AdminLayout = lazy(() => import('./features/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })));
const AdminLoginPage = lazy(() => import('./features/admin/AdminLoginPage').then((m) => ({ default: m.AdminLoginPage })));
const AuditPage = lazy(() => import('./features/admin/AuditPage').then((m) => ({ default: m.AuditPage })));
const EnquiriesPage = lazy(() => import('./features/admin/EnquiriesPage').then((m) => ({ default: m.EnquiriesPage })));
const ManagersPage = lazy(() => import('./features/admin/ManagersPage').then((m) => ({ default: m.ManagersPage })));
const MessagesPage = lazy(() => import('./features/admin/MessagesPage').then((m) => ({ default: m.MessagesPage })));
const OverviewPage = lazy(() => import('./features/admin/OverviewPage').then((m) => ({ default: m.OverviewPage })));
const UsersPage = lazy(() => import('./features/admin/UsersPage').then((m) => ({ default: m.UsersPage })));
const VenuesPage = lazy(() => import('./features/admin/VenuesPage').then((m) => ({ default: m.VenuesPage })));
const BookingsPage = lazy(() => import('./features/bookings/BookingsPage').then((m) => ({ default: m.BookingsPage })));
const PrivacyPage = lazy(() => import('./features/legal/PrivacyPage').then((m) => ({ default: m.PrivacyPage })));
const HallDetailPage = lazy(() => import('./features/halls/HallDetailPage').then((m) => ({ default: m.HallDetailPage })));
const BookingPage = lazy(() => import('./features/booking/BookingPage').then((m) => ({ default: m.BookingPage })));
const ExplorePage = lazy(() => import('./features/explore/ExplorePage').then((m) => ({ default: m.ExplorePage })));
const AboutPage = lazy(() => import('./features/pages/AboutPage').then((m) => ({ default: m.AboutPage })));
const ArticlePage = lazy(() => import('./features/pages/ArticlePage').then((m) => ({ default: m.ArticlePage })));
const InspirationPage = lazy(() => import('./features/pages/InspirationPage').then((m) => ({ default: m.InspirationPage })));
const LocationsPage = lazy(() => import('./features/pages/LocationsPage').then((m) => ({ default: m.LocationsPage })));
const OccasionPage = lazy(() => import('./features/pages/OccasionPage').then((m) => ({ default: m.OccasionPage })));
const OccasionsPage = lazy(() => import('./features/pages/OccasionsPage').then((m) => ({ default: m.OccasionsPage })));
const ManagerPage = lazy(() => import('./features/manager/ManagerPage').then((m) => ({ default: m.ManagerPage })));
const NotificationsPage = lazy(() => import('./features/notifications/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const SubscriptionsPage = lazy(() => import('./features/admin/SubscriptionsPage').then((m) => ({ default: m.SubscriptionsPage })));
const MasterDataPage = lazy(() => import('./features/admin/MasterDataPage').then((m) => ({ default: m.MasterDataPage })));
const AdminBookingsPage = lazy(() => import('./features/admin/BookingsPage').then((m) => ({ default: m.BookingsPage })));

export function App() {
  return (
    <>
      <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route element={<Layout />}>
          {/* The public site: a front page, the venue search, the venue page and
              its step-by-step booking, and the guides around them. */}
          <Route index element={<HomePage />} />
          <Route path="venues" element={<ExplorePage />} />
          <Route path="occasions" element={<OccasionsPage />} />
          <Route path="occasions/:slug" element={<OccasionPage />} />
          <Route path="locations" element={<LocationsPage />} />
          <Route path="inspiration" element={<InspirationPage />} />
          <Route path="inspiration/:slug" element={<ArticlePage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="hall/:id" element={<HallDetailPage />} />
          <Route
            path="hall/:id/book"
            element={
              <RequireAuth roles={['user']}>
                <BookingPage />
              </RequireAuth>
            }
          />
          <Route path="privacy" element={<PrivacyPage />} />

          <Route
            path="bookings"
            element={
              <RequireAuth>
                <BookingsPage />
              </RequireAuth>
            }
          />
          <Route
            path="notifications"
            element={
              <RequireAuth>
                <NotificationsPage />
              </RequireAuth>
            }
          />
          <Route
            path="manager"
            element={
              <RequireAuth roles={['manager', 'admin', 'supervisor']}>
                <ManagerPage />
              </RequireAuth>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Route>

        {/* The admin console is a separate shell with its own front door — it does
            not nest in the booker Layout, so it gets no Browse/Contact chrome and
            no booker login page. */}
        <Route path="admin/login" element={<AdminLoginPage />} />
        <Route
          path="admin"
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          <Route index element={<OverviewPage />} />
          <Route path="managers" element={<ManagersPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="venues" element={<VenuesPage />} />
          <Route path="bookings" element={<AdminBookingsPage />} />
          <Route path="enquiries" element={<EnquiriesPage />} />
          <Route path="messages" element={<MessagesPage />} />
          <Route path="subscriptions" element={<SubscriptionsPage />} />
          <Route path="master" element={<MasterDataPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      </Suspense>
      <VersionBadge />
    </>
  );
}

function NotFound() {
  const { t } = useT();
  return (
    <div className="card">
      <h2>{t('notFoundTitle')}</h2>
      <p className="muted">{t('notFoundBody')}</p>
    </div>
  );
}
